import { formatCurrency } from '../utils/formatting';
import { SalaryCalculationResult, OverseasTerritoryId } from '../types';
import { findOverseasTerritoryById } from '../types';

interface RevenueCardsDisplayProps {
  salaryData: SalaryCalculationResult;
  isAnnual: boolean;
  netAfterTaxWithCustomRate: { annual: number; monthly: number };
  selectedOverseasTerritory?: OverseasTerritoryId;
}

export default function RevenueCardsDisplay({
  salaryData,
  isAnnual,
  netAfterTaxWithCustomRate,
  selectedOverseasTerritory
}: RevenueCardsDisplayProps) {
  const heroAmount = isAnnual ? netAfterTaxWithCustomRate.annual : netAfterTaxWithCustomRate.monthly;
  const heroPeriod = isAnnual ? '/ an' : '/ mois';
  const heroHint = isAnnual
    ? `soit ${formatCurrency(netAfterTaxWithCustomRate.monthly)} par mois`
    : `soit ${formatCurrency(netAfterTaxWithCustomRate.annual)} par an`;

  const grossAmount = isAnnual ? salaryData.grossAnnual : salaryData.grossMonthly;
  const withPrimesAmount = isAnnual ? salaryData.withPrimesAnnual : salaryData.withPrimesMonthly;
  const netBeforeTax = isAnnual ? salaryData.netAfterChargesAnnual : salaryData.netAfterChargesMonthly;

  const territory = selectedOverseasTerritory ? findOverseasTerritoryById(selectedOverseasTerritory) : null;

  return (
    <div className="space-y-4 mb-6">
      <div className="rounded-2xl bg-emerald-600 text-white p-5 md:p-6">
        <div className="text-xs font-semibold tracking-wider uppercase text-emerald-50/90 mb-3">
          Revenu net après impôts
        </div>
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-3xl md:text-4xl font-bold break-all">
            {formatCurrency(heroAmount)}
          </span>
          <span className="text-lg md:text-xl text-emerald-50">{heroPeriod}</span>
        </div>
        <div className="text-sm text-emerald-50/90 mt-2">
          {heroHint}
        </div>
        {territory && (
          <div className="text-xs text-emerald-50/80 mt-1">
            Incluant les avantages territoriaux {territory.name}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 md:p-5">
        <div className="text-xs font-semibold tracking-wider uppercase text-gray-500 mb-3">
          Comment on arrive à ce montant
        </div>
        <div className="grid grid-cols-2 gap-2 md:gap-3">
          <div className="rounded-xl bg-gray-50 p-3">
            <div className="text-[11px] font-medium tracking-wider uppercase text-gray-500 mb-1">
              Brut
            </div>
            <div className="text-base md:text-lg font-bold text-gray-900 break-all">
              {formatCurrency(grossAmount)}
            </div>
          </div>
          <div className="rounded-xl bg-gray-50 p-3">
            <div className="text-[11px] font-medium tracking-wider uppercase text-gray-500 mb-1">
              Avec primes
            </div>
            <div className="text-base md:text-lg font-bold text-gray-900 break-all">
              {formatCurrency(withPrimesAmount)}
            </div>
          </div>
          <div className="rounded-xl bg-gray-50 p-3">
            <div className="text-[11px] font-medium tracking-wider uppercase text-gray-500 mb-1">
              Net avant impôts
            </div>
            <div className="text-base md:text-lg font-bold text-gray-900 break-all">
              {formatCurrency(netBeforeTax)}
            </div>
          </div>
          <div className="rounded-xl bg-emerald-50 border-l-4 border-emerald-500 p-3">
            <div className="text-[11px] font-medium tracking-wider uppercase text-emerald-700 mb-1">
              Net après impôts
            </div>
            <div className="text-base md:text-lg font-bold text-emerald-800 break-all">
              {formatCurrency(heroAmount)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
